import { AppButton, DotMatrix } from '@clickguard/design-system'
import { useState, useCallback, useEffect, ChangeEvent } from 'react'
import { GDialog, GInput } from 'components/basic-blocks'
import { AppConfig } from 'config'
import { useStores } from 'hooks'
import { Assets } from 'services/user/assets'
import { isValidDomain } from 'utils'
import { toast } from 'react-toastify'
import { FiUsers } from 'react-icons/fi'
import { ServerSideTrackingState } from '../../types'
import { DomainInput } from './domain-input'
import { DnsInstructions } from './dns-instructions'

interface ServerSideTabProps {
	onSkip: () => void
	onComplete: () => void
}

export const ServerSideTab = ({ onSkip, onComplete }: ServerSideTabProps) => {
	const {
		assetStore: { asset, setAssetForId },
		accountStore: { account }
	} = useStores()

	const savedState = asset?.server_side_tracking as
		| ServerSideTrackingState
		| undefined

	const [domain, setDomain] = useState(
		savedState?.domain || asset?.domain || ''
	)
	const [subdomain, setSubdomain] = useState(savedState?.subdomain || '')
	const [target, setTarget] = useState(
		savedState?.target ||
			(AppConfig.ENV === 'production'
				? 'pulse.clickguard.com'
				: 'tracker-staging.clickguard.com')
	)
	const [cnameCreated, setCnameCreated] = useState(
		savedState?.cnameCreated || false
	)
	const [domainError, setDomainError] = useState<string | null>(null)
	const [isGenerating, setIsGenerating] = useState(false)
	const [showInviteModal, setShowInviteModal] = useState(false)
	const [inviteEmail, setInviteEmail] = useState('')
	const [isSendingInvite, setIsSendingInvite] = useState(false)
	const [isCompleting, setIsCompleting] = useState(false)
	const [isSkipping, setIsSkipping] = useState(false)

	useEffect(() => {
		if (savedState) {
			if (savedState.domain) setDomain(savedState.domain)
			else setDomain(asset?.domain || '')
			if (savedState.subdomain) setSubdomain(savedState.subdomain)
			if (savedState.target) setTarget(savedState.target)
			if (savedState.cnameCreated !== undefined)
				setCnameCreated(savedState.cnameCreated)
		}
	}, [asset?.id, savedState?.subdomain, savedState?.cnameCreated])

	const handleDomainChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value
			setDomain(value)
			if (domainError) setDomainError(null)
		},
		[domainError]
	)

	const handleGenerate = useCallback(async () => {
		if (!domain.trim()) {
			setDomainError('Domain is required')
			return
		}
		if (!isValidDomain(domain)) {
			setDomainError('Please enter a valid domain (e.g., example.com)')
			return
		}
		if (!account?.id || !asset?.id) {
			toast.error('Account or asset information is missing')
			return
		}

		setDomainError(null)
		setIsGenerating(true)

		try {
			const serverSideTrackingState: ServerSideTrackingState = {
				domain,
				step: '01',
				cnameCreated: true
			}

			const updatedAsset = await Assets.updateAssetDomain(
				account.id,
				asset.id,
				domain,
				serverSideTrackingState
			)

			const responseState = (updatedAsset as unknown as Record<string, unknown>)
				?.server_side_tracking as ServerSideTrackingState | undefined

			if (responseState?.subdomain) {
				setSubdomain(responseState.subdomain)
				if (responseState.target) setTarget(responseState.target)
				setCnameCreated(true)
				await setAssetForId(account.id, asset.id)
				toast.success('DNS record generated successfully')
			} else {
				throw new Error('Failed to generate subdomain from backend')
			}
		} catch (error: unknown) {
			const err = error as {
				response?: { data?: { message?: string } }
				message?: string
			}
			console.error('Failed to generate DNS record:', error)
			toast.error(
				err?.response?.data?.message ||
					err?.message ||
					'Failed to generate DNS record. Please try again.'
			)
		} finally {
			setIsGenerating(false)
		}
	}, [domain, account?.id, asset?.id, setAssetForId])

	const handleRegenerate = useCallback(async () => {
		if (!account?.id || !asset?.id) {
			toast.error('Account or asset information is missing')
			return
		}
		setCnameCreated(false)
		setSubdomain('')
		try {
			const serverSideTrackingState: ServerSideTrackingState = {
				domain,
				step: '01',
				cnameCreated: false
			}

			await Assets.updateAssetDomain(
				account.id,
				asset.id,
				domain,
				serverSideTrackingState
			)

			await setAssetForId(account.id, asset.id)
		} catch (error: unknown) {
			const err = error as {
				response?: { data?: { message?: string } }
				message?: string
			}
			console.error('Failed to regenerate DNS record:', error)
			toast.error(
				err?.response?.data?.message ||
					err?.message ||
					'Failed to reset DNS record. Please try again.'
			)
		}
	}, [domain, account?.id, asset?.id, setAssetForId])

	const handleSkipWithReset = useCallback(async () => {
		if (cnameCreated && subdomain && account?.id && asset?.id) {
			setIsSkipping(true)
			try {
				const serverSideTrackingState: ServerSideTrackingState = {
					domain,
					step: '01',
					cnameCreated: false
				}

				await Assets.updateAssetDomain(
					account.id,
					asset.id,
					domain,
					serverSideTrackingState
				)

				onSkip()
				setAssetForId(account.id, asset.id)
				return
			} catch (error: unknown) {
				const err = error as {
					response?: { data?: { message?: string } }
					message?: string
				}
				console.error('Failed to reset DNS record:', error)
				toast.error(
					err?.response?.data?.message ||
						err?.message ||
						'Failed to reset DNS record. Please try again.'
				)
				setIsSkipping(false)
				return
			}
		}
		onSkip()
	}, [
		cnameCreated,
		subdomain,
		domain,
		account?.id,
		asset?.id,
		setAssetForId,
		onSkip
	])

	const handleMarkComplete = useCallback(async () => {
		if (!account?.id || !asset?.id) return

		setIsCompleting(true)
		try {
			const serverSideTrackingState: ServerSideTrackingState = {
				domain,
				subdomain,
				target,
				step: 'completed',
				cnameCreated: true,
				setupCompleted: true,
				completionDate: new Date().toISOString()
			}

			await Assets.updateAssetDomain(
				account.id,
				asset.id,
				domain,
				serverSideTrackingState
			)
			await setAssetForId(account.id, asset.id)
			onComplete()
		} catch (error) {
			console.error('Failed to mark server-side tracking as complete:', error)
			toast.error('Failed to save. Please try again.')
		} finally {
			setIsCompleting(false)
		}
	}, [
		domain,
		subdomain,
		target,
		account?.id,
		asset?.id,
		setAssetForId,
		onComplete
	])

	const handleSendInvite = useCallback(async () => {
		if (!inviteEmail.trim()) return
		if (!account?.id || !asset?.id) {
			toast.error('Account or asset information is missing')
			return
		}
		setIsSendingInvite(true)
		try {
			await Assets.sendDnsEmail(account.id, asset.id, inviteEmail)
			toast.success('Instructions sent successfully!')
			setInviteEmail('')
			setShowInviteModal(false)
		} catch (error: unknown) {
			const err = error as { response?: { data?: { message?: string } } }
			toast.error(
				err?.response?.data?.message || 'Failed to send. Please try again.'
			)
		} finally {
			setIsSendingInvite(false)
		}
	}, [inviteEmail, account?.id, asset?.id])

	return (
		<div className="flex flex-col">
			{/* Send Instructions Modal */}
			<GDialog
				open={showInviteModal}
				onClose={() => setShowInviteModal(false)}
				showClose={false}
				maxWidth="md"
				className="p-8 px-12 !max-w-[556px]"
			>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-2">
						<h2 className="text-3xl leading-[30px] font-semibold text-t-default">
							Invite a team member to complete this step
						</h2>
						<p className="text-[16px] leading-[22px] text-t-desc-gray">
							We will send an email for them to create an account and continue
							the process.
						</p>
					</div>
					<GInput
						name="invite-email"
						label="Email"
						placeholder="example@clickguard.com"
						type="email"
						value={inviteEmail}
						onChange={(e) => setInviteEmail(e.target.value)}
						labelClassName="text-[14px] leading-[18px] font-medium text-t-google-ad"
						inputClassName="w-full text-[16px] leading-[22px] text-t-coupon"
					/>
					<AppButton
						variant="marketing"
						size="lg"
						fullWidth
						label="Send invite"
						isLoading={isSendingInvite}
						loader="dots"
						disabled={!inviteEmail.trim() || isSendingInvite}
						onClick={handleSendInvite}
					/>
				</div>
			</GDialog>

			{!(cnameCreated && subdomain) && (
				<div className="flex flex-col gap-6">
					<p className="text-[16px] leading-[22px] text-t-checkbox-unactive">
						Get the most accurate click tracking by adding server-side tracking.
						Make sure your domain is correct before generating the instructions.{' '}
						<a
							href="https://docs.clickguard.com/en/articles/21547-serverside-tracking-setup-process"
							target="_blank"
							rel="noopener noreferrer"
							className="text-brand underline"
						>
							Learn more
						</a>
					</p>

					<DomainInput
						domain={domain}
						domainError={domainError}
						isLoading={isGenerating}
						onDomainChange={handleDomainChange}
						onGenerate={handleGenerate}
					/>
				</div>
			)}

			{cnameCreated && subdomain ? (
				<div className="flex flex-col gap-[10px]">
					<DnsInstructions
						subdomain={subdomain}
						domain={domain}
						target={target}
						onRegenerate={handleRegenerate}
					/>

					<div className="flex flex-col gap-4 mt-4">
						<AppButton
							variant="marketing"
							size="lg"
							fullWidth
							label="Mark as complete and proceed"
							isLoading={isCompleting}
							loader="dots"
							disabled={isCompleting}
							onClick={handleMarkComplete}
						/>
					</div>

					<div className="flex items-center justify-between md:h-[37px] md:flex-row flex-col md:gap-0 gap-2 md:mt-0 mt-2">
						<button
							className="flex  items-center gap-[5px] text-[14px] leading-[18px] text-brand hover:underline"
							onClick={() => setShowInviteModal(true)}
						>
							<FiUsers className="w-4 h-4" />
							Send instructions to my developer
						</button>
						<button
							className="text-[14px] leading-[22px] font-medium text-t-desc-gray hover:text-t-default flex items-center gap-2"
							onClick={handleSkipWithReset}
							disabled={isSkipping}
						>
							{isSkipping && <DotMatrix size="xs" />}
							Skip this step for now →
						</button>
					</div>
				</div>
			) : (
				<div className="flex items-center justify-center mt-9">
					<button
						className="text-[14px] leading-[22px] font-medium text-t-desc-gray hover:text-t-default"
						onClick={onSkip}
					>
						Skip this step for now →
					</button>
				</div>
			)}
		</div>
	)
}
